import {
  binColor,
  NOT_DETECTED_COLOR,
  shouldRenderUnclearTriangle,
} from '@resistapp/client/components/plots/legends/heatmap-bins';
import { PlotTooltip, usePlotTooltip } from '@resistapp/client/components/tooltips/plot-tooltip';
import { SvgTooltip } from '@resistapp/client/components/tooltips/svg-tooltip';
import { useAssayContext } from '@resistapp/client/contexts/assay-context';
import { useResearchContext } from '@resistapp/client/contexts/research-context';
import { useSearchParamsContext } from '@resistapp/client/contexts/search-params-context';
import { useUser } from '@resistapp/client/contexts/use-user-context';
import { AbunanceSelection } from '@resistapp/client/data-utils/filter-data/filter';
import { ProjectCtsResponse } from '@resistapp/common/api-types';
import { BarDatum, getBarDatumKeys } from '@resistapp/client/data-utils/plot-data/research-plot-data';
import { useNormalisationMode } from '@resistapp/client/hooks/use-query-filters/use-query-filters';
import { getKeyPressesFromMouseClick } from '@resistapp/client/utils/general';
import { AssayInfo, GeneGrouping, GetGroup } from '@resistapp/common/assays';
import { isAdmin } from '@resistapp/common/features';
import { getSimpleBioNumber } from '@resistapp/common/sampling-points';
import { NormalisationMode, SmartChipFlag } from '@resistapp/common/types';
import { AxisScale } from '@visx/axis';
import { BarStack } from '@visx/shape';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Fragment } from 'react/jsx-runtime';
import { theme } from '../../shared/theme';
import type { KeysPressOptions } from '../legends/legend';
import { GeneBarStackProps, getGroupItemWidthAndMargin } from './data-groups';
import { ScaleBand, ScaleTime } from './multi-plot';
import {
  BarTooltipData,
  GeneInfoTooltipContent,
  getAbundace,
  HeatmapTooltipContent,
  NumDetectedTooltipContent,
} from './tooltips';
import { getSampleSelector, getSemiTransparentColor, VisualSelectionStatus } from './utils';

// Tooltip data of a single bar or heatmap cell. The general flag marks the transparent full-height bar behind the
// detected genes stack, whose tooltip shows the total number of detected genes without gene group info.
type BarRectTooltipData = BarTooltipData & { general?: boolean };

export function GenesBarStack(props: GeneBarStackProps) {
  const {
    datum,
    geneGroups,
    xScale,
    yScale,
    groupWidth,
    groupLength,
    currentGroupLength,
    colorScale,
    isHeatmap,
    sortIdxByGene,
    totalGroupSize,
    bioRepCount,
    j,
    i,
    singleGeneHeatBoxHeight,
    againstTime,
    grouping,
    ordinalAligmentFixup,
    tooltipStuff,
  } = props;
  const {
    queryFilters,
    samplesBeingSelected,
    setSamplesBeingSelected,
    samplingPointIdsForSelectedGroup,
    sampleById,
    getVisualSelectionStatus,
    adminFeatures,
    ctsBySampleId,
  } = useResearchContext();
  const { user } = useUser();
  const { getGroup, allAssays } = useAssayContext();
  const { searchParams } = useSearchParamsContext();
  const normalisationMode = useNormalisationMode();
  // One tooltip instance per sample column: the cells share it, since only one tooltip is ever shown at a time
  const { tooltipData, tooltipProps, handleMouseMove } = usePlotTooltip<BarRectTooltipData>(tooltipStuff);

  const isAdminUser = isAdmin(user);

  const openChip = useCallback(
    (sampleId: number, assay: string) => {
      if (!isAdminUser) return;
      const sample = sampleById.get(sampleId);
      if (!sample) return;
      const chipId = sample.chipIds?.[0];
      if (!chipId) return;
      const bioNumber = getSimpleBioNumber(sample);
      if (!bioNumber) return;
      const base = `${sample.id}-${bioNumber}`;
      const samplesParam = [1, 2, 3].map(rep => `${base}-rep${rep}`).join(',');
      window.open(`/chip/${chipId}?samples=${samplesParam}&ggroups=${assay}`, '_blank');
    },
    [isAdminUser, sampleById],
  );

  const { groupLeftMargin, itemWidth } = getGroupItemWidthAndMargin(groupWidth, groupLength, currentGroupLength);
  const keys = isHeatmap ? getBarDatumKeys(datum) : geneGroups;
  // This checks whether the chart can fit the gene names next to it, in the whitespace.
  // totalGroupSize * 145 is the scaling width of the gene names, and window.innerWidth - 430 is the starting size.
  const fitsInChart = totalGroupSize * 177 < window.innerWidth - 458;
  const isLastAndTimed = againstTime && i === totalGroupSize - 1;
  const showGeneNames =
    isHeatmap && singleGeneHeatBoxHeight === 10 && fitsInChart && j === bioRepCount - 1 && !isLastAndTimed;

  const selectedSamplingPointIdsOrdered = queryFilters.filters.selectedSamplingPointIdsOrdered;
  const visualStatus = getVisualSelectionStatus(datum.samplingPointId);

  const onClick = useCallback(
    (samplingPointId: number, keyPresses: KeysPressOptions) =>
      getSampleSelector(
        selectedSamplingPointIdsOrdered,
        samplingPointIdsForSelectedGroup,
        samplesBeingSelected,
        setSamplesBeingSelected,
        queryFilters.toggleSamplingPointStable,
        true,
        true,
      )(samplingPointId, keyPresses),
    [
      selectedSamplingPointIdsOrdered,
      samplingPointIdsForSelectedGroup,
      samplesBeingSelected,
      setSamplesBeingSelected,
      queryFilters.toggleSamplingPointStable,
    ],
  );

  const getBarColor = useCallback(
    (defaultColor: string) => {
      return visualStatus === VisualSelectionStatus.DESELECTED ? getSemiTransparentColor(defaultColor) : defaultColor;
    },
    [visualStatus],
  );
  const barOpacity = visualStatus !== VisualSelectionStatus.SELECTED ? 0.5 : 1;

  // HACK WHILE measured=false genes are not globally, consistnently included in the data structure / sort index
  // Maintain a local cache for genes that are missing in the pre-computed sortIdxByGene map.
  // Start new indices from the current size of the predefined map – guarantees uniqueness and avoids
  // potential undefined values when computing the maximum.
  // Resolve the vertical index for a given gene key – fall back to sequential indices for genes
  // that are not present in sortIdxByGene (e.g. genes that only have estimated values).
  const rectIdxCacheRef = useRef<Record<string, number>>({});
  const nextRectIdxRef = useRef<number>(Object.keys(sortIdxByGene).length);
  const getRectIdx = useCallback(
    (key: string): number => {
      const predefined = (sortIdxByGene as Record<string, number | undefined>)[key];
      if (predefined !== undefined) return predefined;

      const cached = (rectIdxCacheRef.current as Record<string, number | undefined>)[key];
      if (cached !== undefined) return cached;

      const newIdx = nextRectIdxRef.current++;
      rectIdxCacheRef.current[key] = newIdx;
      return newIdx;
    },
    [sortIdxByGene],
  );

  // Everything the cells of this column need, resolved once per column instead of once per cell
  const cellContext: BarRectRenderContext = {
    isHeatmap,
    againstTime,
    i,
    j,
    xScale,
    ordinalAligmentFixup,
    groupLeftMargin,
    groupWidth,
    itemWidth,
    normalisationMode,
    visualStatus,
    allAssays,
    getGroup,
    selectedTargetGrouping: queryFilters.filters.selectedTargetGrouping,
    abundanceSelection: queryFilters.filters.abundances,
    sneakyEstimations: searchParams.get('estimations') === 'sneaky',
    highlightFlag: isHeatmap && adminFeatures.isModifyAsAdminActive ? adminFeatures.highlightFlag : null,
    ctsBySampleId,
    handleMouseMove,
  };

  return (
    <>
      <BarStack<BarDatum, string>
        data={[datum]}
        keys={keys}
        x={_ => undefined /* calculated below */}
        xScale={xScale}
        yScale={yScale}
        color={barKey => getBarColor(colorScale(barKey))}
        opacity={barOpacity}
      >
        {stackRectangles => {
          return stackRectangles.map((rectangle, index) =>
            rectangle.bars.map(stackDatum => (
              <Fragment key={`brect${rectangle.index}-${rectangle.key}`}>
                {/* This is a rectangle that is full height and resides under the other rects. It contains a tooltip
              with total number of detected genes, making empty samples more noticable */}
                {index === 0 &&
                  !isHeatmap &&
                  renderBarRect(cellContext, {
                    height: 277,
                    y: -1,
                    stackDatum,
                    rectIdx: getRectIdx(rectangle.key),
                    color: 'transparent',
                    general: true,
                  })}
                {renderBarRect(cellContext, {
                  height: isHeatmap ? singleGeneHeatBoxHeight : stackDatum.height,
                  stackDatum,
                  rectIdx: getRectIdx(rectangle.key),
                  onClick: e => {
                    if (isHeatmap && isAdminUser) {
                      // Translate the key (which may be a gene, antibiotic group, etc.) into its corresponding
                      // assay identifier (e.g. "AY1234") so that the chip view receives a valid parameter.
                      const assay = getGroup(rectangle.key, 'assay') ?? rectangle.key;

                      if (assay.startsWith('AY')) {
                        openChip(Number(stackDatum.bar.data.sampleId), assay);
                        return;
                      }
                    }
                    // Fallback: toggle selection behaviour when not opening the chip view.
                    onClick(datum.samplingPointId, getKeyPressesFromMouseClick(e));
                  },
                })}
              </Fragment>
            )),
          );
        }}
      </BarStack>
      {tooltipData && (
        <PlotTooltip {...tooltipProps}>
          {isHeatmap ? (
            <HeatmapTooltipContent grouping={grouping} tooltipData={tooltipData} colorScale={colorScale} />
          ) : (
            <NumDetectedTooltipContent
              tooltipData={tooltipData}
              colorScale={colorScale}
              grouping={grouping}
              showGeneInfo={!tooltipData.general}
            />
          )}
        </PlotTooltip>
      )}
      {showGeneNames && (
        <BarStack<BarDatum, string>
          data={[datum]}
          keys={keys}
          x={_ => undefined /* calculated below */}
          xScale={xScale}
          yScale={yScale}
          color={barKey => getBarColor(colorScale(barKey))}
          opacity={barOpacity}
        >
          {stackRectangles =>
            stackRectangles.map(rectangle =>
              rectangle.bars.map(stackDatum => (
                <BarRectName
                  key={`brect${rectangle.index}-${rectangle.key}`}
                  height={singleGeneHeatBoxHeight}
                  isHeatmap={isHeatmap}
                  againstTime={againstTime}
                  i={i}
                  xScale={xScale}
                  ordinalAligmentFixup={ordinalAligmentFixup}
                  groupLeftMargin={groupLeftMargin}
                  groupWidth={groupWidth}
                  itemWidth={itemWidth}
                  stackDatum={stackDatum}
                  rectIdx={getRectIdx(rectangle.key)}
                />
              )),
            )
          }
        </BarStack>
      )}
    </>
  );
}

interface BarRectRenderContext {
  isHeatmap: boolean;
  againstTime: boolean;
  i: number;
  j: number;
  xScale: AxisScale<number>;
  ordinalAligmentFixup: number;
  groupLeftMargin: number;
  groupWidth: number;
  itemWidth: number;
  normalisationMode: NormalisationMode;
  visualStatus: VisualSelectionStatus;
  allAssays: AssayInfo[];
  getGroup: GetGroup;
  selectedTargetGrouping: GeneGrouping;
  abundanceSelection: AbunanceSelection;
  sneakyEstimations: boolean;
  highlightFlag: SmartChipFlag | null; // Only set when the admin highlight mode applies to this chart
  ctsBySampleId: ProjectCtsResponse['ctsBySampleId'] | undefined;
  handleMouseMove: (
    event: React.MouseEvent | React.TouchEvent,
    data: BarRectTooltipData | undefined,
    enter?: boolean,
  ) => void;
}

interface BarRectCell {
  stackDatum: BarTooltipData;
  rectIdx: number;
  height: number;
  y?: number;
  color?: string;
  general?: boolean;
  onClick?: (e: React.MouseEvent<SVGRectElement>) => void;
}

// Renders one bar or heatmap cell. This is a plain function rather than a component on purpose: the heatmap can have
// hundreds of thousands of cells, and a component with hooks and context subscriptions per cell is what made large
// projects unusably slow.
function renderBarRect(context: BarRectRenderContext, cell: BarRectCell) {
  const {
    isHeatmap,
    againstTime,
    i,
    j,
    xScale,
    ordinalAligmentFixup,
    groupLeftMargin,
    groupWidth,
    itemWidth,
    normalisationMode,
    visualStatus,
    allAssays,
    getGroup,
    selectedTargetGrouping,
    abundanceSelection,
    sneakyEstimations,
    highlightFlag,
    ctsBySampleId,
    handleMouseMove,
  } = context;
  const { height, rectIdx, stackDatum, color, y, general, onClick } = cell;

  const scaledX = againstTime
    ? (xScale as ScaleTime)(new Date(new Date(stackDatum.bar.data.date)))
    : (xScale as ScaleBand)(`${i}`);
  const yUpper = y || (isHeatmap ? rectIdx * height : stackDatum.y);
  const x = ordinalAligmentFixup + groupLeftMargin + j * itemWidth + scaledX - groupWidth / 2;
  const localColor =
    color ||
    (isHeatmap
      ? binColor(
          getAbundace(stackDatum),
          normalisationMode,
          false,
          stackDatum.bar.data.flagByGene?.[stackDatum.key] || null,
        )
      : stackDatum.color);

  const finalFillColor =
    visualStatus === VisualSelectionStatus.DESELECTED ? getSemiTransparentColor(localColor) : localColor;
  const finalOpacity = visualStatus !== VisualSelectionStatus.SELECTED ? 0.5 : 1;
  const flag = stackDatum.bar.data.flagByGene?.[stackDatum.key] || null;
  const shouldDrawUnclearTriangleCell = shouldRenderUnclearTriangle(normalisationMode, flag);
  const baseHeatmapColor =
    visualStatus === VisualSelectionStatus.DESELECTED
      ? getSemiTransparentColor(NOT_DETECTED_COLOR)
      : NOT_DETECTED_COLOR;

  const shouldDrawFlagRectangle = (() => {
    if (!isHeatmap || !highlightFlag) return false;
    const assay = getGroup(stackDatum.key, 'assay') ?? stackDatum.key;
    const cts = ctsBySampleId?.[stackDatum.bar.data.sampleId]?.[assay] ?? [];
    return cts.some(ct => ct.flags?.split(/[,.;\s]+/).includes(highlightFlag as string));
  })();

  const measuredByGene = stackDatum.bar.data.measuredByGene;

  const estimateBorderInfo = (() => {
    if (sneakyEstimations) {
      return { draw: false, color: '#FFF' } as const;
    }

    if (abundanceSelection !== AbunanceSelection.MEASURED_OR_ESTIMATE) {
      return { draw: false, color: '#FFF' } as const;
    }

    if (!measuredByGene || measuredByGene[stackDatum.key]) {
      return { draw: false, color: '#FFF' } as const;
    }

    // Gene is unmeasured, so we draw a border. Choose darker if an estimated value exists.
    const val = stackDatum.bar.data[stackDatum.key] as number | null | undefined;
    const isEstimatedDetected = !!val && val > 0.00001;
    const borderColor = isEstimatedDetected ? '#AAA' : '#EEE';
    return { draw: true, color: borderColor } as const;
  })();

  const tooltipDatum: BarRectTooltipData = general ? { ...stackDatum, general: true } : stackDatum;
  const onMouseLeave = (event: React.MouseEvent) => {
    handleMouseMove(event, undefined);
  };
  const onMouseMove = (event: React.MouseEvent) => {
    if (selectedTargetGrouping === 'assay') {
      const assay = allAssays.find(a => a.assay === stackDatum.key);
      if (assay) {
        handleMouseMove(event, {
          ...tooltipDatum,
          label: allAssays.find(a => a.assay === assay.assay)?.gene,
        });
      }
    } else {
      handleMouseMove(event, tooltipDatum);
    }
  };
  const onMouseEnter = (event: React.MouseEvent) => {
    handleMouseMove(event, tooltipDatum, true);
  };

  return (
    <>
      {shouldDrawUnclearTriangleCell ? (
        <>
          <rect x={x} y={yUpper} height={height} width={itemWidth} fill={baseHeatmapColor} opacity={finalOpacity} />
          <polygon
            points={`${x},${yUpper} ${x + itemWidth},${yUpper} ${x},${yUpper + height}`}
            fill={finalFillColor}
            opacity={finalOpacity}
            pointerEvents="none"
          />
          <rect
            x={x}
            y={yUpper}
            height={height}
            width={itemWidth}
            fill="transparent"
            onMouseLeave={onMouseLeave}
            onMouseMove={onMouseMove}
            onMouseEnter={onMouseEnter}
            style={{ cursor: onClick ? 'pointer' : 'default' }}
            onClick={onClick}
          />
        </>
      ) : (
        <rect
          x={x}
          y={yUpper}
          height={height}
          width={itemWidth}
          fill={finalFillColor}
          opacity={finalOpacity}
          onMouseLeave={onMouseLeave}
          onMouseMove={onMouseMove}
          onMouseEnter={onMouseEnter}
          style={{ cursor: onClick ? 'pointer' : 'default' }}
          onClick={onClick}
        />
      )}
      {shouldDrawFlagRectangle && (
        <rect
          x={x}
          y={yUpper}
          height={height}
          width={itemWidth}
          fill="none"
          stroke="#000"
          strokeWidth={1}
          strokeOpacity={visualStatus === VisualSelectionStatus.DESELECTED ? 0.1 : 1}
          pointerEvents="none"
          shapeRendering="crispEdges"
        />
      )}
      {estimateBorderInfo.draw && (
        <rect
          x={x}
          y={yUpper}
          height={height}
          width={itemWidth}
          fill="none"
          stroke={estimateBorderInfo.color}
          strokeWidth={1}
          strokeOpacity={visualStatus === VisualSelectionStatus.DESELECTED ? 0.1 : 1}
          pointerEvents="none"
          shapeRendering="crispEdges"
        />
      )}
    </>
  );
}

interface BarRectNameProps {
  height: number;
  rectIdx: number;
  isHeatmap: boolean;
  againstTime: boolean;
  i: number;
  xScale: AxisScale<number>;
  ordinalAligmentFixup: number;
  groupLeftMargin: number;
  groupWidth: number;
  itemWidth: number;
  stackDatum: BarTooltipData;
  y?: number;
}

function BarRectName(props: BarRectNameProps) {
  const {
    height,
    rectIdx,
    isHeatmap,
    againstTime,
    i,
    xScale,
    ordinalAligmentFixup,
    groupLeftMargin,
    groupWidth,
    itemWidth,
    stackDatum,
    y,
  } = props;
  const scaledX = againstTime
    ? (xScale as ScaleTime)(new Date(new Date(stackDatum.bar.data.date)))
    : (xScale as ScaleBand)(`${i}`);
  const yUpper = y || (isHeatmap ? rectIdx * height : stackDatum.y);
  const x = ordinalAligmentFixup + groupLeftMargin + scaledX - groupWidth / 2 + 51;

  const [show, setShow] = useState(false);
  const textRef = useRef<SVGTextElement>(null);
  const [textWidth, setTextWidth] = useState(0);
  useLayoutEffect(() => {
    if (textRef.current) {
      const w = textRef.current.getBBox().width;
      if (w && w !== textWidth) setTextWidth(w);
    }
  }, [stackDatum.key, textWidth]);

  if (stackDatum.key === 'sampleNumber') {
    return null;
  }
  return (
    <g
      onMouseEnter={() => {
        setShow(true);
      }}
      onMouseLeave={() => {
        setShow(false);
      }}
    >
      {/* Invisible overlay to extend hover area so mouse can move towards the tooltip without leaving */}
      <rect x={x} y={yUpper} width={160} height={height} fill="transparent" />
      <text
        ref={textRef}
        x={x}
        y={yUpper + 7}
        height={height}
        width={itemWidth}
        fill={theme.colors.neutral500}
        fontSize={10}
        style={{ cursor: 'default' }}
      >
        {stackDatum.key}
      </text>
      {show && (
        <SvgTooltip
          x={x + 120}
          y={yUpper + 100}
          content={<GeneInfoTooltipContent assay={stackDatum.key} />}
          anchorBottomLeft
          onMouseLeave={() => {
            setShow(false);
          }}
        />
      )}
    </g>
  );
}
